import { BulbOutlined, EditOutlined, EyeInvisibleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Prompts } from "@ant-design/x";
import { Button, Space, theme, Typography } from "antd";
import { AnimationEventHandler, ReactElement, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";
import useQuickResponses from "../../hooks/useQuickResponses";
import { mapToSimplifiedItem } from "../../model/nextBestAction";
import { selectCopilotAccess } from "../../redux/agentCopilotAccessSlice";
import { useLazyGetNextBestActionQuery } from "../../redux/apis/agentCopilotApi";
import { TranscriptHubContext } from "../providers/TranscriptHubProvider";

const { useToken } = theme;

export interface SuggestedAnswerContainerProps {
  onEditBeforeSend(answer: string): void;
  showQuickResponses: boolean;
  showSuggestedAnswer: boolean;
  onHideSuggestedAnswer(): void;
  onHideQuickResponses(): void;
}

export default function SuggestedAnswerContainer({ onEditBeforeSend, showQuickResponses, showSuggestedAnswer, onHideSuggestedAnswer, onHideQuickResponses }: SuggestedAnswerContainerProps): ReactElement | null {
  const { transcriptItems, sendTextMessage } = useContext(TranscriptHubContext);
  const { copilotId } = useSelector(selectCopilotAccess);
  const { t } = useTranslation();
  const { token } = useToken();

  // #region Suggested answer
  const [getNextBestAction, { isFetching: isFetchingSuggestedAnswer }] = useLazyGetNextBestActionQuery();

  const [suggestedAnswer, setSuggestedAnswer] = useState("");
  const [suggestedAnswerAnimation, setSuggestedAnswerAnimation] = useState<SuggestedAnswerAnimationState>("initial");
  const [suppressSuggestionsUntilTurn, setSuppressSuggestionsUntilTurn] = useState<number | null>(null);

  const { quickResponses, processingQuickResponseId, sendQuickResponse, isReactive } = useQuickResponses();

  const lastMessage = transcriptItems.length > 0 ? transcriptItems[transcriptItems.length - 1] : null;
  const isLastFromCustomer = lastMessage?.source.participant === "Customer" && lastMessage?.translatedText !== null;

  const generateSuggestedAnswer = useCallback(
    () => {
      if (!copilotId || transcriptItems.length === 0) return;
      const simplifiedItems = transcriptItems.map(mapToSimplifiedItem);
      getNextBestAction({ data: { transcript: simplifiedItems } })
        .unwrap()
        .then((response) => {
          setSuggestedAnswer(response.answer);
          setSuggestedAnswerAnimation("initial");
        })
        .catch(() => {});
    }, [copilotId, getNextBestAction, transcriptItems]);

  useEffect(
    () => {
      if (!copilotId || transcriptItems.length === 0 || !showSuggestedAnswer || isReactive)
        return;

      if (isLastFromCustomer) {
        const simplifiedItems = transcriptItems.map(mapToSimplifiedItem);
        const request = getNextBestAction({ data: { transcript: simplifiedItems } });

        request.unwrap()
          .then((response) => {
            setSuggestedAnswer(response.answer);
            setSuggestedAnswerAnimation("initial");
          })
          .catch(() => {});

        return () => request?.abort();
      }
    },
    [copilotId, getNextBestAction, transcriptItems, showSuggestedAnswer, isReactive, isLastFromCustomer],
  );

  useEffect(
    () => {
      if (!isLastFromCustomer) {
        setSuggestedAnswer("");
        setSuggestedAnswerAnimation("initial");
      }
    },
    [isLastFromCustomer],
  );

  useEffect(
    () => {
      if (showSuggestedAnswer && suggestedAnswerAnimation !== "initial") {
        setSuggestedAnswerAnimation("initial");
      }
    },
    [showSuggestedAnswer, suggestedAnswerAnimation],
  );

  const handleSuggestionClick = useCallback(
    () => {
      if (!suggestedAnswer || suggestedAnswerAnimation !== "initial")
        return;

      sendTextMessage(suggestedAnswer);
      setSuggestedAnswerAnimation("send");
    },
    [sendTextMessage, suggestedAnswer, suggestedAnswerAnimation],
  );

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      if (!suggestedAnswer || suggestedAnswerAnimation !== "initial")
        return;

      e.stopPropagation();
      onEditBeforeSend(suggestedAnswer);
      setSuggestedAnswerAnimation("edit");
      setSuppressSuggestionsUntilTurn(transcriptItems.length);
    },
    [onEditBeforeSend, suggestedAnswer, suggestedAnswerAnimation, transcriptItems.length],
  );

  const handleClearSuggestion = useCallback(
    (e: React.MouseEvent) => {
      if (suggestedAnswerAnimation !== "initial") return;

      e.stopPropagation();
      setSuggestedAnswer("");
      onHideSuggestedAnswer();
      setSuggestedAnswerAnimation("clear");
    },
    [suggestedAnswerAnimation, onHideSuggestedAnswer],
  );

  const handleSuggestionAnimationEnd = useCallback<AnimationEventHandler<HTMLDivElement>>(
    (e) => {
      if (e.animationName.includes("suggestedAnswer")) // do not react to antd's own animations
        setSuggestedAnswer("");
    },
    [],
  );
  // #endregion

  useEffect(
    () => {
      if (suppressSuggestionsUntilTurn !== null && transcriptItems.length > suppressSuggestionsUntilTurn) {
        setSuppressSuggestionsUntilTurn(null);
      }
    },
    [transcriptItems.length, suppressSuggestionsUntilTurn],
  );

  return (
    <SuggestedAnswerContainerBox>
      {
        (showQuickResponses && quickResponses.length > 0) &&
        <ShortcutPrompts
          wrap
          title={
            <Space>
              <span>✨ {t("agentAssist.quickResponses")}</span>
              <HideShortcutsButton
                icon={<EyeInvisibleOutlined />}
                onClick={onHideQuickResponses}
                aria-label={t("agentAssist.hideQuickResponses")}
              />
            </Space>
          }
          items={
            quickResponses.map(qr => ({
              key: qr.id,
              description: (
                <Space>
                  {processingQuickResponseId === qr.id && <LoadingOutlined />}
                  <span>{qr.displayName}</span>
                </Space>
              ),
              disabled: processingQuickResponseId !== null,
            }))
          }
          onItemClick={({ data }) => sendQuickResponse(data.key)}
        />
      }
      {
        showSuggestedAnswer && isLastFromCustomer && !isReactive && suppressSuggestionsUntilTurn === null &&
        <>
          {
            (suggestedAnswer || isFetchingSuggestedAnswer || suggestedAnswerAnimation !== "initial") &&
            <SuggestionPrompts
              $animationState={suggestedAnswerAnimation}
              onAnimationEnd={handleSuggestionAnimationEnd}
              items={[{
                key: "suggested-answer",
                icon: <BulbOutlined style={{ color: token.gold }} />,
                label: (
                  <SuggestedAnswerTitle>
                    <span>{t("agentAssist.suggestedAnswer")}</span>
                    <Space>
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={handleEditClick}
                      >
                        {t("agentAssist.edit")}
                      </Button>
                      <HideShortcutsButton
                        icon={<EyeInvisibleOutlined />}
                        size="small"
                        onClick={handleClearSuggestion}
                        aria-label={t("agentAssist.dismissSuggestedAnswer")}
                      />
                    </Space>
                  </SuggestedAnswerTitle>
                ),
                description: isFetchingSuggestedAnswer
                  ? <LoadingOutlined />
                  : <SuggestedAnswerContent ellipsis={{ rows: 5, tooltip: suggestedAnswer }}>{suggestedAnswer}</SuggestedAnswerContent>,
              }]}
              onItemClick={handleSuggestionClick}
            />
          }
        </>
      }
      {
        showSuggestedAnswer && isLastFromCustomer && isReactive && suppressSuggestionsUntilTurn === null && (
          <>
            <SuggestionPrompts
              $animationState={suggestedAnswerAnimation}
              onAnimationEnd={handleSuggestionAnimationEnd}
              items={[
                {
                  key: "manual-suggested-answer",
                  icon: <BulbOutlined style={{ color: token.gold }} />,
                  label: (
                    <SuggestedAnswerTitle>
                      <span>{t("agentAssist.suggestedAnswer")}</span>
                      <Space>
                        {suggestedAnswer && (
                          <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={handleEditClick}
                            disabled={
                              suggestedAnswerAnimation !== "initial"
                            }
                          >
                            {t("agentAssist.edit")}
                          </Button>
                        )}
                        {!suggestedAnswer && (
                          <Button
                            type="primary"
                            size="small"
                            onClick={generateSuggestedAnswer}
                            disabled={isFetchingSuggestedAnswer}
                          >
                            {isFetchingSuggestedAnswer
                              ? <LoadingOutlined />
                              : t("agentAssist.generateLabel")}
                          </Button>
                        )}
                        <HideShortcutsButton
                          icon={<EyeInvisibleOutlined />}
                          size="small"
                          onClick={handleClearSuggestion}
                          aria-label={t("agentAssist.dismissSuggestedAnswer")}
                        />
                      </Space>
                    </SuggestedAnswerTitle>
                  ),
                  description: suggestedAnswer
                    ? <SuggestedAnswerContent ellipsis={{ rows: 5, tooltip: suggestedAnswer }}>{suggestedAnswer}</SuggestedAnswerContent>
                    : t("agentAssist.generateText"),
                },
              ]}
              onItemClick={suggestedAnswer ? handleSuggestionClick : undefined}
            />
          </>
        )
      }
    </SuggestedAnswerContainerBox>
  );
}

const SuggestedAnswerContainerBox = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 ${p => p.theme.paddingXXS}px;
  flex: 0 0 auto;

  display: flex;
  align-items: end;
  gap: ${p => p.theme.paddingSM}px;
`;

type SuggestedAnswerAnimationState = "initial" | "send" | "edit" | "clear";

const SuggestionPrompts = styled(Prompts)<{ $animationState: SuggestedAnswerAnimationState }>`
  margin-left: auto;
  flex: 0 1 auto;
  min-width: 0;

  .ant-prompts-item {
    max-width: 100%;
  }

  .ant-prompts-label {
    width: 100%;
  }

  @keyframes suggestedAnswer-slideUp {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    100% {
      transform:  translateY(-100%);
      opacity: 0;
    }
  }

  @keyframes suggestedAnswer-disappear {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes suggestedAnswer-slideRight {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    100% {
      transform:  translateX(100%);
      opacity: 0;
    }
  }

  ${p => p.$animationState === "send" && css`animation: suggestedAnswer-slideUp;`}
  ${p => p.$animationState === "edit" && css`animation: suggestedAnswer-disappear;`}
  ${p => p.$animationState === "clear" && css`animation: suggestedAnswer-slideRight;`}
  animation-duration: ${p => p.theme.motionDurationMid};
  animation-timing-function: ${p => p.theme.motionEaseInOut};
  animation-fill-mode: forwards;
`;

const SuggestedAnswerTitle = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: ${p => p.theme.paddingSM}px;
`;

const SuggestedAnswerContent = styled(Typography.Paragraph)`
  &&& {
    margin: 0;
  }
`;

const ShortcutPrompts = styled(Prompts)`
  flex: 0 1 auto;
  min-width: auto;

  h5 {
    white-space: nowrap;
    min-width: fit-content;
  }

  .ant-prompts-item {
    padding: ${p => p.theme.paddingXXS}px ${p => p.theme.paddingSM}px;
  }
`;

const HideShortcutsButton = styled(Button).attrs({ type: "link", size: "small" })`
  color: ${p => p.theme.colorTextDescription} !important;

  &:hover {
    color: ${p => p.theme.colorTextPlaceholder} !important;
  }
`;
